/**
 * useRememberUsStream ✦ iter211
 * ─────────────────────────────────────────────────────────────
 * Live wall sync with SSE primary + short-poll fallback.
 *
 * Architectural contract (Craig, iter211):
 *
 *   • SSE primary — EventSource with delta-only `?since=` so the
 *     server NEVER re-replays history on reconnect (avoids Safari
 *     reconnect storms, tablet memory churn, duplicate paints).
 *   • Short-poll fallback — kicks in only when SSE fails 3x in a
 *     row OR EventSource isn't available. Same delta-only `?since=`
 *     contract so the wire shape is identical.
 *   • Heartbeat-aware — Safari can silently kill an SSE socket;
 *     we treat >25s with no event as a stall and reconnect.
 *   • Dedupe by stroke_id — the catch-up burst can overlap with
 *     the initial GET in the consumer's boot path, and that's fine
 *     because we only emit strokes the consumer hasn't seen.
 *
 * onStroke({stroke}) fires for every NEW stroke (server or local).
 * onEpoch({epoch}) fires if the server flips wall_epoch (admin
 * clear lands in iter214; the consumer should reset its ledger).
 */
import { useEffect, useRef } from 'react';

const STALL_MS = 25_000;
const POLL_INTERVAL_MS = 2_000;
const RECONNECT_BACKOFF_MS = [500, 1000, 2000, 4000];

export function useRememberUsStream({
  apiBase,
  slug,
  enabled = true,
  initialSince = '1970-01-01T00:00:00+00:00',
  onStroke,
  onEpoch,
  onTransport, // 'sse' | 'poll' | 'idle' — purely diagnostic, never user-facing
}) {
  const sinceRef = useRef(initialSince);
  const seenIdsRef = useRef(new Set());
  const esRef = useRef(null);
  const pollTimerRef = useRef(null);
  const stallTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const cancelledRef = useRef(false);
  const transportRef = useRef('idle');

  // Stable refs to caller callbacks so the effect doesn't re-fire
  // on every consumer render.
  const onStrokeRef = useRef(onStroke);
  const onEpochRef = useRef(onEpoch);
  const onTransportRef = useRef(onTransport);
  useEffect(() => { onStrokeRef.current = onStroke; }, [onStroke]);
  useEffect(() => { onEpochRef.current = onEpoch; }, [onEpoch]);
  useEffect(() => { onTransportRef.current = onTransport; }, [onTransport]);

  useEffect(() => {
    if (!enabled || !apiBase || !slug) return undefined;
    cancelledRef.current = false;

    const setTransport = (t) => {
      if (transportRef.current === t) return;
      transportRef.current = t;
      try { onTransportRef.current?.(t); } catch { /* ignore */ }
    };

    const acceptStroke = (s) => {
      if (!s || !s.stroke_id) return;
      if (seenIdsRef.current.has(s.stroke_id)) return;
      seenIdsRef.current.add(s.stroke_id);
      if (s.created_at && s.created_at > sinceRef.current) {
        sinceRef.current = s.created_at;
      }
      try { onStrokeRef.current?.(s); } catch { /* consumer crash never breaks the stream */ }
    };

    const resetStallTimer = () => {
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
      stallTimerRef.current = setTimeout(() => {
        if (cancelledRef.current) return;
        // Stall — Safari likely silently killed the socket. Force
        // a reconnect through the SSE path; if SSE fails again we
        // fall through to polling.
        openSSE();
      }, STALL_MS);
    };

    const closeSSE = () => {
      if (esRef.current) {
        try { esRef.current.close(); } catch { /* idempotent */ }
        esRef.current = null;
      }
    };

    const stopPolling = () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const pollOnce = async () => {
      if (cancelledRef.current) return;
      try {
        const url = `${apiBase}/api/remember-us/events/${slug}/strokes?since=${encodeURIComponent(sinceRef.current)}`;
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) throw new Error(`poll ${res.status}`);
        const body = await res.json();
        if (body?.strokes?.length) {
          for (const s of body.strokes) acceptStroke(s);
        }
        setTransport('poll');
      } catch {
        // swallow — schedule next attempt anyway
      } finally {
        if (!cancelledRef.current) {
          pollTimerRef.current = setTimeout(pollOnce, POLL_INTERVAL_MS);
        }
      }
    };

    const startPolling = () => {
      closeSSE();
      stopPolling();
      setTransport('poll');
      pollOnce();
    };

    const openSSE = () => {
      if (cancelledRef.current) return;
      stopPolling();
      closeSSE();
      if (typeof window === 'undefined' || typeof window.EventSource !== 'function') {
        startPolling();
        return;
      }
      const url = `${apiBase}/api/remember-us/events/${slug}/stream?since=${encodeURIComponent(sinceRef.current)}`;
      let es;
      try { es = new window.EventSource(url); }
      catch { startPolling(); return; }
      esRef.current = es;
      resetStallTimer();

      es.addEventListener('open', () => {
        reconnectAttemptRef.current = 0;
        setTransport('sse');
      });
      es.addEventListener('stroke', (ev) => {
        resetStallTimer();
        try {
          const parsed = JSON.parse(ev.data);
          acceptStroke(parsed);
        } catch { /* malformed — ignore */ }
      });
      es.addEventListener('epoch', (ev) => {
        try {
          const e = JSON.parse(ev.data);
          // Wall epoch flipped — consumer must reset its ledger.
          seenIdsRef.current = new Set();
          sinceRef.current = '1970-01-01T00:00:00+00:00';
          try { onEpochRef.current?.(e); } catch { /* ignore */ }
        } catch { /* ignore */ }
      });
      es.addEventListener('error', () => {
        // EventSource's built-in retry is unreliable on Safari — we
        // close it ourselves and reconnect with backoff. After
        // RECONNECT_BACKOFF_MS attempts we give up and poll.
        closeSSE();
        const attempt = reconnectAttemptRef.current;
        reconnectAttemptRef.current = attempt + 1;
        if (attempt >= RECONNECT_BACKOFF_MS.length) {
          startPolling();
          return;
        }
        const delay = RECONNECT_BACKOFF_MS[attempt];
        setTimeout(() => { if (!cancelledRef.current) openSSE(); }, delay);
      });
    };

    openSSE();

    return () => {
      cancelledRef.current = true;
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
      closeSSE();
      stopPolling();
      setTransport('idle');
    };
  }, [apiBase, slug, enabled]);

  return {
    seedSeen(strokeIds) {
      for (const id of strokeIds || []) seenIdsRef.current.add(id);
    },
    seedSince(iso) {
      if (iso && iso > sinceRef.current) sinceRef.current = iso;
    },
    transport: () => transportRef.current,
  };
}

export default useRememberUsStream;
